import { MotionDiv } from '@/components/element/MotionDiv';
import { useSalesOrders } from '@/hooks/api/1210_sales_orders';
import { SalesOrder } from '@/hooks/api/models';
import { useLoading } from '@/providers/LoadingProvider';
import { useSearchCondition } from '@/providers/SearchConditionProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import { DataTable, DataTableSortEvent } from 'primereact/datatable';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTableSortField } from '@/components/element/types';
import { storage } from '@/lib/storage';
import { useDataTableSortContent } from '@/hooks/useDataTableSortContent';

export const SalesOrdersListElement = () => {
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { setShowLoading } = useLoading();
  const { salesOrderSearchCondition: searchCondition, setSalesOrderSearchCondition: setSearchCondition } =
    useSearchCondition();
  const { data, isLoading, refetch } = useSalesOrders({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    per: 5000,
    ...searchCondition,
    enabled: false,
  });

  useEffect(() => {
    refetch();
  }, [searchCondition]);

  useEffect(() => setShowLoading(isLoading), [isLoading]);

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const titleTemplate = (so: SalesOrder) => (
    <>
      {so.alert != null && (
        <div className="text-sm mb-1">
          <span className="badge badge-sm badge-danger">{so.alert}</span>
        </div>
      )}
      <div>{so.name}</div>
    </>
  );
  const phaseTemplate = (so: SalesOrder) => so.team_sales_order_phase.label;
  const actualSalesTemplate = (so: SalesOrder) => `¥${so.accounting_total_amount.toLocaleString()}`;

  const assignTemplate = (so: SalesOrder) => (
    <div className="d-flex align-items-center">
      <img
        src={so.user.user_account.icon_file_url}
        className="avatar avatar-xs me-2 rounded-circle"
        alt="avatar image"
      />
      <h6 className="mb-0 text-xxs">{so.user.user_account.name}</h6>
    </div>
  );

  const contractStartDateTemplate = (so: SalesOrder) => (so.contract_start_date ? so.contract_start_date : '-');
  const contractEndDateTemplate = (so: SalesOrder) => (so.contract_end_date ? so.contract_end_date : '-');

  // DataTableのソート、キーワード検索
  const { dataTableSortContent, onSort, onKeywordChanged } = useDataTableSortContent(
    `sales_order_${_w_!.id}_${_t_!.id}`
  );

  // 検索ワードの設定
  useEffect(() => {
    let _filters = { ...filters };
    _filters.global.value = dataTableSortContent.dataTableSearchKeyword?.keyword || '';
    setFilters(_filters);
  }, [dataTableSortContent]);

  if (data == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">プロジェクト（売上）一覧</h5>
        </div>
        <div>
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              className="form-control"
              placeholder="検索"
              type="text"
              value={filters.global.value}
              onChange={(e) => {
                const value = e.target.value;
                let _filters = { ...filters };
                _filters.global.value = value;
                setFilters(_filters);
                onKeywordChanged(value);
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-body">
        {data.sales_orders.length == 0 && (
          <p className="card-description text-sm">登録されているプロジェクト（売上）がありません。</p>
        )}

        {data.sales_orders.length > 0 && (
          <div className="table-responsive">
            <DataTable
              value={data.sales_orders}
              stripedRows
              scrollable
              scrollHeight="800px"
              tableClassName="table data-table align-items-center clickable-row"
              removableSort
              onRowClick={(e) => {
                navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${e.data.id}`);
              }}
              filters={filters}
              globalFilterFields={[
                'alert',
                'name',
                'contract_start_date',
                'contract_end_date',
                'user.user_account.name',
                'business_partner.display_name',
                'team_sales_order_phase.label',
              ]}
              emptyMessage="No Data."
              onSort={onSort}
              sortField={dataTableSortContent.dataTableSortField?.filed}
              sortOrder={dataTableSortContent.dataTableSortField?.sortOrder}
            >
              <Column
                header="取引先"
                field="business_partner.display_name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '100px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="プロジェクト名"
                field="name"
                body={titleTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '230px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                // style={{ minWidth: '200px' }}
                // bodyStyle={{ backgroundColor: 'inherit', whiteSpace: 'normal', wordWrap: 'break-word' }}
                frozen
                sortable
              ></Column>
              <Column
                header="契約開始日"
                field="contract_start_date"
                body={contractStartDateTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="契約終了日"
                field="contract_end_date"
                body={contractEndDateTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="フェーズ"
                field="team_sales_order_phase.label"
                body={phaseTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                style={{ minWidth: '50px' }}
                sortable
              ></Column>
              <Column
                header="金額"
                field="accounting_total_amount"
                body={actualSalesTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold text-end pe-4"
                headerClassName="text-uppercase text-xxs font-weight-bolder text-end"
                style={{ width: '50px' }}
                sortable
              ></Column>
              <Column
                header="担当者"
                field="user.user_account.name"
                body={assignTemplate}
                bodyClassName="text-xxs px-2"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
            </DataTable>
          </div>
        )}
      </div>
    </MotionDiv>
  );
};
